<template>
  <div data-private>
  <PageHeader
    :title="patientData.bene_first_name ? `${patientData.bene_first_name} ${patientData.bene_last_name}` : 'Patient'"
    :breadcrumbs="[{ label: 'Patient', to: '/patients' }]"
    title-class="sensitive"
  >
    <!-- Patient Info Summary -->
    <div class="border rounded-md bg-white/30 backdrop-blur-xs shadow-md">
      <div class="px-6 mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ">
        <div>
          <dt class="text-sm font-medium text-gray-500">Patient ID</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="identifier">{{ patientIdentifier }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">MBI ID</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="identifier">{{ patientData.bene_mbi_id || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Date of Birth</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="dob">{{ formatDate(patientData.bene_dob) || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Sex</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ formatSex(patientData.bene_sex_cd) || '—' }}</dd>
        </div>
      </div>

      <!-- Additional Patient Details -->
      <div class="px-6 mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <dt class="text-sm font-medium text-gray-500">Practice</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="practice">{{ patientData.practice_name || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Location</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="facility">{{ patientData.location_name || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Primary NPI</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="identifier">{{ patientData.primary_npi || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Primary TIN</dt>
          <dd class="sensitive mt-1 text-sm text-gray-900" data-spoof-kind="identifier">{{ patientData.primary_tin || '—' }}</dd>
        </div>
      </div>

      <!-- Performance Period Info -->
      <div class="px-6 mt-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <dt class="text-sm font-medium text-gray-500">Performance Year</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ patientData.performance_year || '—' }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Performance Period</dt>
          <dd class="mt-1 text-sm text-gray-900">
            {{ formatDate(patientData.performance_period_start) }} - {{ formatDate(patientData.performance_period_end) }}
          </dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">Assignment Type</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ patientData.primary_assign_type || '—' }}</dd>
        </div>
      </div>

      <!-- Status Badges -->
      <div class="px-6 pb-4 flex items-center gap-3">
        <router-link
          :to="`/patients/${patientIdentifier}/claims`"
          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
        >
          Claims Explorer →
        </router-link>
        <span v-if="patientData.bene_death_dt"
              class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
          <!-- One inline wrapper keeps the space after the label: in this
               flex badge a space at the end of a text item collapses. -->
          <span> Deceased: <span class="sensitive" data-spoof-kind="date">{{ formatDate(patientData.bene_death_dt) }}</span></span>
        </span>
        <span v-if="patientData.other_patient_identifier"
              class="sensitive inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
          {{ patientData.other_patient_identifier_type }}: {{ patientData.other_patient_identifier }}
        </span>
        <span v-if="patientData.fhir_patient_id"
              class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
          <span> FHIR ID: <span class="sensitive" data-spoof-kind="identifier">{{ patientData.fhir_patient_id }}</span></span>
        </span>
      </div>
    </div>

    <template #bottom>
      <div class="px-6 pt-4 border-b border-gray-200 mx-6">
        <Page_Tabs
          :initial-tab="selectedTab"
          :tabs="availableTabs"
          no-border
          @select="selectTab"
        />
      </div>
    </template>

  </PageHeader>

  <div class="min-h-screen">

    <!-- Content Area -->
    <div class="px-4 py-4">
      <!-- Graph -->
      <div v-if="selectedTab === 'Graph'" style="height: calc(100vh - 280px); min-height: 600px;">
        <Patient_Subgraph
          :patient-identifier="patientIdentifier"
          :key="`subgraph-${patientIdentifier}`"
        />
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <!-- Attribution Data -->
        <div v-if="selectedTab === 'Attribution'">
          <ComponentHeader title="Attribution Data" />
          <div class="p-6">
            <Patient_Attribution_Table
              :patient-identifier="patientIdentifier"
              :key="`attrib-${patientIdentifier}`"
            />
          </div>
        </div>

        <!-- CCLF Part B Data -->
        <div v-if="selectedTab === 'CCLF Part B'">
          <ComponentHeader title="CCLF Part B Data" />
          <div class="p-6">
            <Patient_CCLF_PartB_Table
              :patient-identifier="patientIdentifier"
              :key="`cclf-partb-${patientIdentifier}`"
            />
          </div>
        </div>

        <!-- CCLF Part A Data -->
        <div v-if="selectedTab === 'CCLF Part A'">
          <ComponentHeader title="CCLF Part A Data" />
          <div class="p-6">
            <Patient_CCLF_PartA_Table
              :patient-identifier="patientIdentifier"
              :key="`cclf-parta-${patientIdentifier}`"
            />
          </div>
        </div>

        <!-- BCDA -->
        <div v-if="selectedTab === 'BCDA'">
          <ComponentHeader title="BCDA Data" />
          <div class="p-6">
            <Details_Patient_BCDA_Table
              :patient-identifier="patientIdentifier"
              :key="`bcda-${patientIdentifier}`"
            />
          </div>
        </div>

        <!-- EHR Claims -->
        <div v-if="selectedTab === 'EHR Claims'">
          <ComponentHeader title="EHR Claims (Pre-Adjudicated)" />
          <div class="p-6">
            <Details_Patient_EHR_Claims
              :patient-identifier="patientIdentifier"
              :key="`ehr-claims-${patientIdentifier}`"
            />
          </div>
        </div>

        <!-- Appointments -->
        <div v-if="selectedTab === 'Appointments'">
          <ComponentHeader title="Appointments" />
          <div class="p-6">
            <Details_Patient_Appointments
              :patient-identifier="patientIdentifier"
              :key="`appointments-${patientIdentifier}`"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import Page_Tabs from '@/components/widgetcomponents/Page_Tabs.vue';
import Patient_Attribution_Table from '@/components/Details/Patient_Data/Details_Patient_Attribution.vue';
import Patient_CCLF_PartB_Table from '@/components/Details/Patient_Data/Details_Patient_CCLF_PartB_Table.vue';
import Patient_CCLF_PartA_Table from '@/components/Details/Patient_Data/Details_Patient_CCLF_PartA_Table.vue';
import Details_Patient_BCDA_Table from "@/components/Details/Patient_Data/Details_Patient_BCDA_Table.vue";
import Details_Patient_Appointments from "@/components/Details/Patient_Data/Details_Patient_Appointments.vue";
import Details_Patient_EHR_Claims from "@/components/Details/Patient_Data/Details_Patient_EHR_Claims.vue";
import Patient_Subgraph from "@/components/Patient_Drawer/Tabs/Patient_Subgraph.vue";
import { parseDate } from '@/utils/common';

import spiralDots from '@/assets/spiral_dots.svg';
import { COMPANY } from '@/constants';
import axios from "axios";
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';

export default defineComponent({
  name: 'Overview_Patient',
  components: {
    PageHeader,
    ComponentHeader,
    Details_Patient_BCDA_Table,
    Page_Tabs,
    Patient_Attribution_Table,
    Patient_CCLF_PartB_Table,
    Patient_CCLF_PartA_Table,
    Details_Patient_Appointments,
    Details_Patient_EHR_Claims,
    Patient_Subgraph
  },
  props: {
    patientIdentifier: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      selectedTab: localStorage.getItem('selectedPatientTab') || 'Attribution',
      availableTabs: ['Graph', 'Attribution', 'CCLF Part B','CCLF Part A', 'BCDA', 'EHR Claims', 'Appointments'],
      patientData: {},
      loading: false,
  backgroundImage: spiralDots
    };
  },
  mounted() {
    this.fetchPatientData()
  },
  methods: {
    // Demo Mode: dates tagged data-spoof-kind="date" inside this record
    // share one offset (an opaque key; nothing is bound outside Demo Mode).
    async fetchPatientData() {
      this.loading = true;

      try {
        const response = await axios.post('api/returnpatientdata/', {
          data_type: 'patient_info',
          patient_identifier: this.patientIdentifier,
        });

        this.patientData = response.data.reduce((acc, curr) => {
            Object.keys(curr).forEach(key => {
              if (curr[key] !== null && curr[key] !== undefined && acc[key] === undefined) {
                acc[key] = curr[key];
              }
            });
            return acc;
          }, {});

        // Surface the patient's name in the BROWSER TAB (not just the on-page
        // header). The router sets a generic "PatientDetail | Kairon Health"
        // title on navigation, before the name is known; overwrite it once the
        // demographics load so open tabs are distinguishable (IHCI 7/1).
        const name = [this.patientData.bene_first_name, this.patientData.bene_last_name]
          .filter(Boolean).join(' ').trim();
        if (name && !this.$.isUnmounted) document.title = `${name} | ${COMPANY.NAME}`;
      } catch (error) {
        console.error('Error fetching attribution data:', error);
        this.patientData = {};
      } finally {
        this.loading = false;
      }
    },
    selectTab(tab) {
      this.selectedTab = tab;
      localStorage.setItem('selectedPatientTab', tab);
    },
    // DOB, death date and performance period are calendar dates. BENE_DOB is a
    // TIMESTAMP_NTZ column, so the API may send "1937-01-01T00:00:00" (or a
    // UTC "...Z" form); read only the date part so no timezone can shift it.
    formatDate(dateString) {
      if (!dateString) return null;
      const date = parseDate(String(dateString).slice(0, 10));
      // Unparseable values fall back to the em dash, not "Invalid Date".
      if (isNaN(date)) return null;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    formatSex(sexCode) {
      const sexMap = {
        '1': 'Male',
        '2': 'Female',
      };
      return sexMap[sexCode] || 'Unknown';
    }
  }
});
</script>

